import React from 'react';
import { Eyebrow, Heading, Text } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';

import { getAssetUrl } from 'src/services/http/user';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  eyebrowText: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ title, subtitle, eyebrowText }) => (
  <section css={heroSectionStyles} data-testid="role-based-hero-section">
    <div css={textContentStyles}>
      <div css={eyebrowContainerStyles} data-testid="hero-eyebrow-text">
        <Eyebrow size="medium" fontWeight="semibold" color={token('color.text.subtle')}>
          {eyebrowText}
        </Eyebrow>
      </div>
      <div css={headerStyles} data-testid="hero-title">
        <Heading level={0}>{title}</Heading>
      </div>
      <div css={subtitleStyles} data-testid="hero-subtitle">
        <Text size="large">{subtitle}</Text>
      </div>
    </div>
    <div
      data-testid="hero-section-image"
      css={heroImageStyles}
      style={
        {
          '--desktop-image': `url(${getAssetUrl('/images/hubs/roleBasedHeroBannerDesktop.png')})`,
          '--tablet-image': `url(${getAssetUrl('/images/hubs/roleBasedHeroBannerTablet.png')})`,
          '--mobile-image': `url(${getAssetUrl('/images/hubs/roleBasedHeroBannerMobile.png')})`,
        } as Record<string, string>
      }
    ></div>
  </section>
);

const heroSectionStyles = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  paddingTop: token('space.600'),
  flexDirection: 'column',
  alignItems: 'center',
});

const textContentStyles = css({
  maxWidth: '1022px',
  padding: `0 ${token('space.300')}`,
  textAlign: 'center',

  [media.below.xs]: {
    padding: `0 ${token('space.200')}`,
  },
});

const eyebrowContainerStyles = css({
  marginBottom: token('space.200'),
});

const headerStyles = css({
  marginBottom: token('space.200'),
  textAlign: 'center',
});

const subtitleStyles = css({
  maxWidth: '720px',
  marginBottom: token('space.600'),
  textAlign: 'center',
});

const heroImageStyles = css({
  height: '316px',
  width: '100%',
  boxSizing: 'border-box',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundImage: 'var(--desktop-image)',

  [media.below.md]: {
    height: '210px',
    backgroundImage: 'var(--tablet-image)',
  },

  [media.below.xs]: {
    height: '152px',
    backgroundImage: 'var(--mobile-image)',
  },
});
